import React from "react";
import { ArrowRight } from "lucide-react";
import { apiGetPublic } from "../../Api/Client.js";
import { Button, Checkbox } from "../../Ui/index.jsx";
import "../../Styles/CultureCheck.css";

// The first screen of the joining sequence: what this company expects, read and
// agreed to before confidentiality and before the offer. Opened by a candidate
// who has no account yet, so it renders for signed-out visitors.
//
// One page rather than a deck. Every point is a checkbox, the next appears once
// the current one is ticked, and the explanation collapses the moment it is —
// so nothing goes unread while the page stays short.
//
// The variant is NEVER asked. Department decides engineering or sales,
// employment type decides junior or full-time, and both already sit on the
// person's offer. Until this is attached to an offer they arrive as query
// parameters; when it is attached, the offer token replaces them.
//
// House style for the copy: the hard sentence goes first and the reasoning
// follows it. The point is to be logical rather than punishing — a person
// should finish each point understanding WHY it is the standard, not merely
// that it is. Quotations are the business's own language; they carry no dates,
// because a date turns a standard into a minuted remark.

const POINTS = [
  {
    id: "skill",
    title: "I am here for skill growth first. Compensation matters, secondarily.",
    quote: "We are a tech-hungry company, a skill-hungry company. We are hiring people who are skill-hungry, who are ready to pounce.",
    body: "That order is deliberate, and it is not a slogan. Skill is what produces growth, and growth is what produces better compensation — in that sequence, reliably. Nobody is asking you to be indifferent to what you earn. We are asking you to be honest about what earns it. If you expect the compensation to arrive first and the skill to follow, this is the one assumption we cannot support.",
  },
  {
    id: "hours",
    title: "I will put in the hours to upskill myself, and finish what I take on with ownership.",
    quote: "Everybody is on their own. There is no one going forward who is going to track you. Every week the targets are your own.",
    body: "If you lack a skill today, that is an opportunity, not a disqualification. But an opportunity stays yours only while you deliver against it — and if you do not, it goes to somebody who will. That is not a punishment; the work simply still has to be done. So the extra hours you put into your own learning are expected, and they are the part you keep: once a skill is real, difficult work becomes ordinary, and ordinary work becomes muscle memory.",
  },
  {
    id: "english",
    title: "⚠ English and LLMs are the air here. Neither will be taught.",
    quote: "Context engineering is a basic expectation — one of the basic expectations to live in 2026, in order to succeed in professional lives.",
    body: "Reading, writing and speaking English — and doing all three with a language model, catching its wordplay, its hallucinations and its confident-sounding nonsense — is part of the work rather than a bonus on top of it. It will not be taught here. If you are not already exceptionally good at this, or not able to improve at extraordinary speed, please do not go further. In return, keeping the team at the cutting edge of these tools is treated as the business's responsibility, not yours alone.",
  },
  {
    id: "adult",
    title: "I will conduct myself as an adult.",
    quote: "For our business, it is now or never. For the team, it is now or never.",
    body: "That is the position we are actually in, and it sets the pace for everything else. Everyone here is over eighteen and is treated accordingly — told the truth directly, trusted without supervision, and expected to raise a problem before it becomes one. The pressure is deliberate and it is not hazing: three to four years of learning are compressed into six months, and that only works if nobody has to be managed like a child.",
  },
  {
    id: "value",
    title: "The business can only sustain my employment if I bring positive business value.",
    quote: {
      eng: "What is the solution and the impact for the customer? Not the technology.",
      sales: "You need to sit with the sales team, and I'm not your best friend. You have to get closures.",
    },
    body: "This is simple arithmetic rather than a warning. Salaries are paid out of value created for customers, so a role continues while the work it does is worth more than it costs. That is true of every business; most of them simply do not say it out loud. We would rather you knew the terms of the arrangement than discovered them.",
  },
  {
    id: "solve",
    title: {
      eng: "I will solve real business problems and bring my homework with me.",
      sales: "I will solve real customer problems and bring my homework with me.",
    },
    quote: "Don't ask why, how, what without homework. By the quality of the questions that you ask, I will understand whether you understood.",
    body: {
      eng: "We give you the problem — a real one, with a customer and an outcome attached. What we do not hand you is the context. You are expected to build that yourself, working with a language model in plain English until you genuinely understand the problem, and only then bringing a solution. Nobody will sit beside you while you do it: if you want mentorship, the model is a better teacher than a busy person, and it is available at three in the morning. And when you do bring a question to a human, bring the work behind it — do not ask why, how or what of a person before you have asked it of an LLM.",
      sales: "We give you the problem — a real customer with a real pain, and an outcome we need. The method is decoded and you will be trained on it, but the context of that particular customer, their market and what actually hurts them is yours to build, working with a language model in plain English before you ever get on the call. Nobody will sit beside you while you do it: if you want mentorship, the model is a better teacher than a busy person, and it is available at three in the morning. And when you bring a question to a human, bring the work behind it — do not ask why, how or what of a person before you have asked it of an LLM.",
    },
  },
  {
    id: "quality",
    title: {
      eng: "I own the quality of what I ship, including what the model gave me.",
      sales: "I own the quality of what I put in front of a customer, including what the model gave me.",
    },
    quote: {
      eng: "For every single task, I need you to defend everything that you ship. Stop solving the wrong problem.",
      sales: "Don't go for the solution. We ideally need to attack the pain.",
    },
    body: "A language model will always return something. It returns something whether your understanding was complete or hopeless, and it will sound equally confident either way — so everything it gives you has to be verified before it becomes your work. The quality of what comes out is set by the quality of what went in: how well you understand the customer, the market and who else is competing for that customer. Get the context wrong and the model will help you solve the wrong problem beautifully, which is worse than solving nothing, because it looks finished. There is no separate layer of people checking your work into shape.",
  },
  {
    id: "dates",
    title: "A commitment I make is a date, not an intention.",
    quote: {
      eng: "If you are delaying your commitments, if your releases are getting delayed, deliveries will get delayed — you will be held accountable.",
      sales: "If you are delaying your commitments, deliveries will get delayed — you will be held accountable.",
    },
    body: "Missing a date happens to everyone, and it is survivable — as long as you learn from it and get better at judging your own dates next time. Missing it quietly is the part that is not, because somebody downstream has already planned around your word.",
  },
  {
    id: "growth",
    title: {
      junior: "My growth is a planned, written ladder — and the climbing is mine to do.",
      full: "My growth is mine to drive, and my work is to outgrow my work.",
    },
    quote: {
      junior: "I will give you two, three, six months — because I respect you here.",
      full: "We don't want you to continue to work on the skill that you have. Take up higher responsibilities once you have proved yourself — devoting 10, 20% of time to offloading your current responsibilities to other people.",
    },
    body: {
      junior: "This business has been running for over ten years and has employed more than five hundred juniors and full-timers in that time, so the path from where you are to where you want to be is a known one, not an experiment. The steps in your role, and what each one pays, are written down before you start. What we cannot do is climb it for you. Keep your side of it — the continuous self-upskilling — and the ladder is there and well defined. Let that slip and you stagnate, and a stagnant role is one the business eventually cannot carry.",
      full: "This business has been running for over ten years and has employed more than five hundred people in that time, so it knows what scalable growth looks like and can point you at it. What it will not do is drive it for you. A raise here is a promise against what you will learn next, which is why it is discussed in terms of the skill it buys. Keep that promise and the ladder is well defined. Break it and the role stagnates — and a stagnant role is one the business eventually cannot carry.",
    },
  },
  {
    id: "exit",
    title: "If this is not for me, I will say so — and the earlier the better.",
    quote: "If somebody thinks they do not fit the culture, they are free to cleanly exit. You're here on your own accord, no one forced you. If you think you are not a right fit, I really respect that.",
    body: "Be aware that we are asking the same question about you, urgently, through your first and second month. This offer is not by itself a promise of continuing employment. The chain is short and it runs one way: fast, AI-aided solutions that actually work, leading to positive business outcomes, leading to continuous upskilling, leading to continuing employment. Deciding early costs both of us far less than a polite yes carried for a year. Leaving well is respected here and is never held against you; leaving badly — going quiet, abandoning open work — is the only version that is.",
  },
];


const variantOf = (value, track, seniority) => {
  if (typeof value !== "object" || value === null) return value;
  if ("eng" in value) return value[track];
  if ("junior" in value) return value[seniority];
  return value;
};

// The culture points as a component, so the standalone review page and the real
// joining flow render THE SAME screen rather than two copies that drift.
export function CulturePoints({ track, seniority, firstName, checked, onToggle, busy, onContinue, joined = false, continueLabel = "Continue" }) {
  const total = POINTS.length;
  const count = POINTS.filter((p) => checked[p.id]).length;
  const allRead = count === total;
  // Only one unread point is shown at a time, so the page never opens as a wall
  // of text. Everything already read stays above it, collapsed.
  const visible = Math.min(total, count + 1);

  return (
    <>
      <header className="Cc-Head">
        <span className="Cc-Kicker">
          {joined
            ? "Read once, so we all work from the same page"
            : <>Welcome{firstName ? ` ${firstName}` : ""} &mdash; let&rsquo;s get a few things straight before you accept the offer</>}
        </span>
        <h1>How Banao Technologies actually works.</h1>
        <p>
          We build and run our own products &mdash;{" "}
          <a href="https://interviewgod.ai" target="_blank" rel="noopener noreferrer">InterviewGod</a>,{" "}
          <a href="https://vidya.online" target="_blank" rel="noopener noreferrer">Vidya</a>,{" "}
          <a href="https://vikaas.ai" target="_blank" rel="noopener noreferrer">Vikaas</a> and{" "}
          <a href="https://axonhq.ai" target="_blank" rel="noopener noreferrer">Axon</a> &mdash; alongside the
          client work that comes through{" "}
          <a href="https://banao.tech" target="_blank" rel="noopener noreferrer">Banao Technologies</a>.
          Several products at once, each in a market that moves, means the work is rarely maintenance and
          almost always invention. It asks for people who can think and act responsibly, rather than people
          who want to be assigned.
        </p>
        <p>
          What follows is how that plays out day to day, in the business&rsquo;s own words. Read each point
          and tick it.{" "}
          {joined
            ? "If any of it is unclear, ask your manager before you tick it."
            : "If any of it does not sound like you, stop here rather than going on — let us not make the mistake of accepting an offer, because we understand this environment is not for everyone."}{" "}
          It is for the very hungry, who are willing to learn, work and grow into the very best.
        </p>
      </header>

      <ol className="Cc-List">
        {POINTS.slice(0, visible).map((p, i) => {
          const isDone = Boolean(checked[p.id]);
          return (
            <li key={p.id} className={`Cc-Item${isDone ? " is-done" : ""}${i === visible - 1 && !isDone ? " is-current" : ""}`}>
              <Checkbox
                className="Cc-Agree"
                checked={isDone}
                onChange={() => onToggle(p.id)}
                label={<span className="Cc-Title">{variantOf(p.title, track, seniority)}</span>}
              />
              {!isDone && (
                <div className="Cc-Detail">
                  <blockquote>{variantOf(p.quote, track, seniority)}</blockquote>
                  <p>{variantOf(p.body, track, seniority)}</p>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="Cc-Foot">
        <div className="Cc-Progress" aria-hidden="true">
          <i style={{ width: `${(100 * count) / total}%` }} />
        </div>
        <div className="Cc-Foot-Row">
          <span className="Cc-Count">{count} of {total} read</span>
          <div className="Cc-Actions">
            <Button variant="primary" disabled={!allRead || busy} onClick={onContinue}>
              {continueLabel} <ArrowRight size={16} />
            </Button>
          </div>
        </div>
        <p className="Cc-Hint">
          {allRead && !joined ? "Next: confidentiality, then your offer." : "Tick each point to carry on. Nothing here is a trick question."}
        </p>
      </div>
    </>
  );
}

// Standalone preview of the culture screen, addressed by offer token. The real
// sequence lives at /join/<token>; this exists so the copy can be read on its
// own without walking a candidate through the whole flow.
export function CultureCheckScreen() {
  const token = React.useMemo(() => {
    const m = window.location.pathname.match(/\/onboarding\/culture\/([^/?#]+)/);
    return m ? m[1] : "";
  }, []);
  const [ctx, setCtx] = React.useState(null);
  const [phase, setPhase] = React.useState(token ? "loading" : "invalid");
  const [checked, setChecked] = React.useState({});

  React.useEffect(() => {
    if (!token) return;
    apiGetPublic(`/MainApp/offer/culture/${token}`)
      .then((r) => {
        if (!r || !r.valid) { setPhase("invalid"); return; }
        setCtx(r);
        setPhase(r.track ? "ready" : "skip");
      })
      .catch(() => setPhase("invalid"));
  }, [token]);

  // No verified culture content for this department, so there is nothing to show: go straight on.
  // A redirect is a side effect, so it runs here and never while the screen is being drawn.
  React.useEffect(() => {
    if (phase === "skip") window.location.replace(`/join/${token}`);
  }, [phase, token]);

  if (phase === "loading") return <section className="Cc-Wrap"><p className="Cc-Plain">Loading…</p></section>;
  if (phase === "invalid") {
    return (
      <section className="Cc-Wrap">
        <h1 className="Cc-Plain-Head">This link is not valid.</h1>
        <p className="Cc-Plain">It may have expired, or already been used. Please contact whoever sent you the offer.</p>
      </section>
    );
  }
  if (phase === "skip") return null;

  return (
    <section className="Cc-Wrap">
      <CulturePoints
        track={ctx.track === "sales" ? "sales" : "eng"}
        seniority={ctx.seniority === "full" ? "full" : "junior"}
        firstName={ctx.first_name || ""}
        checked={checked}
        onToggle={(id) => setChecked((d) => ({ ...d, [id]: !d[id] }))}
        busy={false}
        onContinue={() => window.location.assign(`/join/${token}`)}
      />
    </section>
  );
}
